import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Ship, Plane, Plus, Eye, Clock, MapPin, Package, Filter, Search, Anchor, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const vesselTypes = [
  { value: 'container', label: 'Container Ship' },
  { value: 'bulk_carrier', label: 'Bulk Carrier' },
  { value: 'tanker', label: 'Tanker' },
  { value: 'roro', label: 'RoRo' },
  { value: 'tug', label: 'Tug' },
  { value: 'barge', label: 'Barge' },
  { value: 'general_cargo', label: 'General Cargo' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'chemical_tanker', label: 'Chemical Tanker' },
  { value: 'oil_tanker', label: 'Oil Tanker' },
  { value: 'lpg_carrier', label: 'LPG Carrier' },
  { value: 'lng_carrier', label: 'LNG Carrier' },
  { value: 'car_carrier', label: 'Car Carrier' },
  { value: 'heavy_lift', label: 'Heavy Lift' },
  { value: 'offshore_supply', label: 'Offshore Supply' },
  { value: 'multipurpose', label: 'Multipurpose' }
];

const Charters = () => {
  const navigate = useNavigate();
  const [charterRFQs, setCharterRFQs] = useState([]);
  const [myCharterRFQs, setMyCharterRFQs] = useState([]);
  const [myCharterQuotes, setMyCharterQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');
  const [filters, setFilters] = useState({
    charterType: '',
    vesselType: '',
    search: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCharterRFQs();
    if (token) {
      fetchMyCharterRFQs();
      fetchMyCharterQuotes();
    }
  }, [token]);

  const fetchCharterRFQs = async () => {
    try {
      let url = `${API}/api/charter-rfqs?status=open`;
      if (filters.charterType) url += `&charter_type=${filters.charterType}`;
      if (filters.vesselType) url += `&vessel_type=${filters.vesselType}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setCharterRFQs(data);
      }
    } catch (error) {
      console.error('Error fetching charter RFQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyCharterRFQs = async () => {
    try {
      const res = await fetch(`${API}/api/charter-rfqs/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyCharterRFQs(data);
      }
    } catch (error) {
      console.error('Error fetching my charter RFQs:', error);
    }
  };

  const fetchMyCharterQuotes = async () => {
    try {
      const res = await fetch(`${API}/api/charter-quotes/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyCharterQuotes(data);
      }
    } catch (error) {
      console.error('Error fetching my charter quotes:', error);
    }
  };

  const getVesselTypeLabel = (type) => {
    const found = vesselTypes.find(v => v.value === type);
    return found ? found.label : type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      awarded: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const CharterRFQCard = ({ rfq, showActions = false }) => (
    <Card className="hover:shadow-lg transition-shadow" data-testid={`charter-rfq-${rfq.id}`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            {rfq.charter_type === 'vessel' ? (
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Ship className="h-6 w-6 text-cyan-600" />
              </div>
            ) : (
              <div className="p-2 bg-purple-100 rounded-lg">
                <Plane className="h-6 w-6 text-purple-600" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-lg">{rfq.title}</h3>
              <p className="text-sm text-slate-500">{rfq.company_name}</p>
            </div>
          </div>
          {getStatusBadge(rfq.status)}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <MapPin className="h-4 w-4" />
            <span>{rfq.origin_port} → {rfq.destination_port}</span>
          </div>
          {rfq.vessel_type_required && (
            <div className="flex items-center gap-2 text-slate-600">
              <Anchor className="h-4 w-4" />
              <span>{getVesselTypeLabel(rfq.vessel_type_required)}</span>
            </div>
          )}
          {rfq.cargo_quantity && (
            <div className="flex items-center gap-2 text-slate-600">
              <Package className="h-4 w-4" />
              <span>{rfq.cargo_quantity.toLocaleString()} {rfq.cargo_unit}</span>
            </div>
          )}
          {rfq.laycan_start && (
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="h-4 w-4" />
              <span>Laycan: {new Date(rfq.laycan_start).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {rfq.cargo_description && (
          <p className="text-sm text-slate-600 mb-4 line-clamp-2">{rfq.cargo_description}</p>
        )}

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" /> {rfq.views_count} views
            </span>
            <span>{rfq.quotes_count} quotes</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/charters/${rfq.id}`)}
            >
              View Details
            </Button>
            {token && rfq.status === 'open' && !showActions && (
              <Button
                size="sm"
                className="bg-cyan-500 hover:bg-cyan-600"
                onClick={() => navigate(`/charters/${rfq.id}/quote`)}
              >
                Submit Quote
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Page Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Vessel & Cargo Charters</h1>
              <p className="text-slate-600 mt-1">Request or bid on vessel and cargo flight charters</p>
            </div>
            {token && (
              <Button
                className="bg-cyan-500 hover:bg-cyan-600"
                onClick={() => navigate('/charters/create')}
                data-testid="create-charter-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Request Charter
              </Button>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="browse" data-testid="browse-tab">Browse Charters</TabsTrigger>
            {token && (
              <>
                <TabsTrigger value="my-requests" data-testid="my-requests-tab">My Requests</TabsTrigger>
                <TabsTrigger value="my-quotes" data-testid="my-quotes-tab">My Quotes</TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Browse Tab */}
          <TabsContent value="browse">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      placeholder="Search charters..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="w-full"
                    />
                  </div>
                  <Select
                    value={filters.charterType}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, charterType: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Charter Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="vessel">Vessel Charter</SelectItem>
                      <SelectItem value="cargo_flight">Cargo Flight</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={filters.vesselType}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, vesselType: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Vessel Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vessels</SelectItem>
                      {vesselTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchCharterRFQs}>
                    <Filter className="h-4 w-4 mr-2" />
                    Apply
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Charter RFQ List */}
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : charterRFQs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Ship className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Charter Requests</h3>
                  <p className="text-slate-500 mb-4">Be the first to request a vessel or cargo charter</p>
                  {token && (
                    <Button onClick={() => navigate('/charters/create')}>
                      Create Charter Request
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6">
                {charterRFQs.map(rfq => (
                  <CharterRFQCard key={rfq.id} rfq={rfq} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Requests Tab */}
          <TabsContent value="my-requests">
            {myCharterRFQs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Ship className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Charter Requests Yet</h3>
                  <p className="text-slate-500 mb-4">Create your first charter request</p>
                  <Button onClick={() => navigate('/charters/create')}>
                    Request Charter
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6">
                {myCharterRFQs.map(rfq => (
                  <CharterRFQCard key={rfq.id} rfq={rfq} showActions />
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Quotes Tab */}
          <TabsContent value="my-quotes">
            {myCharterQuotes.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <DollarSign className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Quotes Submitted</h3>
                  <p className="text-slate-500 mb-4">Browse charter requests and submit your quotes</p>
                  <Button onClick={() => setActiveTab('browse')}>
                    Browse Charters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myCharterQuotes.map(quote => (
                  <Card key={quote.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold">{quote.vessel_name || quote.aircraft_type}</h4>
                          <p className="text-sm text-slate-500">
                            Rate: ${quote.freight_rate?.toLocaleString()} {quote.rate_unit}
                          </p>
                        </div>
                        <Badge className={
                          quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          quote.status === 'declined' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>
                          {quote.status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </>
  );
};

export default Charters;
